import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import { AccountBalance, Payment, Error as ErrorIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '@/store';
import { useNavigate } from 'react-router-dom';

const CONTACT_EMAIL = import.meta.env.VITE_CONTACT_EMAIL || 'support@banking-dashboard.com';

const Withdraw: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { creditCards, balances } = useSelector((state: RootState) => state.user);
  const { isLoading } = useSelector((state: RootState) => state.user);

  const [formData, setFormData] = useState({
    amount: '',
    currency: 'EUR' as 'EUR' | 'USD',
    creditCardId: '',
    description: '',
    beneficiary: '',
    accountNumber: '',
    bankName: '',
    swiftBic: '',
    country: '',
    address: '',
    phoneNumber: '',
    cardType: 'VISA' as 'VISA' | 'MASTER',
    cardNumber: '',
    nameOnCard: '',
    expiryDate: '',
    cvv: '',
  });

  const [method, setMethod] = useState<'card' | 'bank'>('card');

  const [error, setError] = useState<string | null>(null);

  const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    (e as any).stopPropagation();
    setError(null);

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (method === 'card') {
      // Allow using a saved credit card (creditCardId) OR manual card entry
      if (!formData.creditCardId) {
        if (!formData.cardType || !formData.cardNumber || !formData.nameOnCard || !formData.expiryDate || !formData.cvv) {
          setError('Please select a credit card or fill in all card details');
          return;
        }
      }
    }

    if (method === 'bank') {
      if (!formData.beneficiary || !formData.accountNumber || !formData.bankName || !formData.swiftBic || !formData.country || !formData.address || !formData.phoneNumber) {
        setError('Please fill in all bank transfer details');
        return;
      }
    }

    // Submit withdrawal request
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('/api/user/withdraw', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          amount: parseFloat(formData.amount),
          currency: formData.currency,
          creditCardId: formData.creditCardId || 0,
          description: formData.description,
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        const tx = data.data?.transaction;
        // Backend may create a transaction with status 'failed' but still return success
        if (!tx || tx.status === 'failed') {
          setError(`TRANSACTION FAILED. CONTACT ${CONTACT_EMAIL} FOR MORE DETAILS.`);
        } else {
          setError('Withdrawal submitted successfully.');
        }
      } else {
        setError(data.message || 'Withdrawal failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const getBalance = (currency: 'EUR' | 'USD') => {
    const balance = balances?.find(b => b.currency === currency);
    return balance ? balance.balance : 0;
  };

  const formatCurrency = (amount: number, currency: 'EUR' | 'USD') => {
    const symbol = currency === 'EUR' ? '€' : '$';
    return `${symbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Withdraw Funds
        </Typography>

        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }} 
            icon={<ErrorIcon />}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {creditCards.length === 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You need to add a credit card before making a withdrawal.
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate('/dashboard/credit-cards')}
            >
              Add Credit Card
            </Button>
          </Alert>
        ) : (
          <Box component="div">
            <Grid container spacing={3} alignItems="stretch">
              {/* Top: balances as a full-width section */}
              <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Card sx={{
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      color: 'white',
                      borderRadius: { xs: 8, sm: 12 },
                      boxShadow: '0 12px 40px rgba(99,102,241,0.08)',
                    }}>
                      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box sx={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <AccountBalance sx={{ color: 'white' }} />
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.75rem', fontWeight: 700 }}>EUR Balance</Typography>
                            <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5 }}>{formatCurrency(getBalance('EUR'), 'EUR')}</Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Card sx={{
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      borderRadius: { xs: 8, sm: 12 },
                      boxShadow: '0 12px 40px rgba(16,185,129,0.06)',
                    }}>
                      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box sx={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <AccountBalance sx={{ color: 'white' }} />
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.75rem', fontWeight: 700 }}>USD Balance</Typography>
                            <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5 }}>{formatCurrency(getBalance('USD'), 'USD')}</Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>

              {/* Full-width withdraw form below balances */}
              <Grid item xs={12} sx={{ display: 'flex' }}>
                <Card sx={{ flex: 1, borderRadius: { xs: 8, sm: 12 }, boxShadow: '0 10px 36px rgba(16,24,40,0.06)' }}>
                  <CardContent sx={{ p: { xs: 2.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box sx={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(99,102,241,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Payment sx={{ color: '#8b5cf6' }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" fontWeight={700}>Withdraw Funds</Typography>
                        <Typography variant="caption" color="textSecondary">Withdraw funds from your account</Typography>
                      </Box>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel>Currency</InputLabel>
                          <Select
                            name="currency"
                            value={formData.currency}
                            onChange={handleSelectChange}
                            label="Currency"
                            disabled={isLoading}
                          >
                            <MenuItem value="EUR">EUR</MenuItem>
                            <MenuItem value="USD">USD</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Chip
                            label="Card"
                            clickable
                            color={method === 'card' ? 'primary' : 'default'}
                            onClick={() => setMethod('card')}
                          />
                          <Chip
                            label="Bank"
                            clickable
                            color={method === 'bank' ? 'primary' : 'default'}
                            onClick={() => setMethod('bank')}
                          />
                        </Box>
                      </Grid>

                      {method === 'card' ? (
                        <>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <InputLabel>Currency</InputLabel>
                              <Select
                                name="currency"
                                value={formData.currency}
                                onChange={handleSelectChange}
                                label="Currency"
                                disabled={isLoading}
                              >
                                <MenuItem value="EUR">EUR (Balance: {getBalance('EUR').toFixed(2)})</MenuItem>
                                <MenuItem value="USD">USD (Balance: {getBalance('USD').toFixed(2)})</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <Chip
                                label="VISA"
                                clickable
                                color={formData.cardType === 'VISA' ? 'primary' : 'default'}
                                onClick={() => setFormData(prev => ({ ...prev, cardType: 'VISA' }))}
                                sx={{ px: 2, py: 1 }}
                              />
                              <Chip
                                label="MASTER"
                                clickable
                                color={formData.cardType === 'MASTER' ? 'primary' : 'default'}
                                onClick={() => setFormData(prev => ({ ...prev, cardType: 'MASTER' }))}
                                sx={{ px: 2, py: 1 }}
                              />
                            </Box>
                          </Grid>

                          {/* Saved card select (optional) */}
                          {creditCards && creditCards.length > 0 && (
                            <Grid item xs={12}>
                              <FormControl fullWidth>
                                <InputLabel>Use Saved Card (optional)</InputLabel>
                                <Select
                                  name="creditCardId"
                                  value={formData.creditCardId}
                                  onChange={handleSelectChange}
                                  label="Use Saved Card (optional)"
                                  disabled={isLoading}
                                >
                                  <MenuItem value="">Enter new card</MenuItem>
                                  {creditCards.map((card) => (
                                    <MenuItem key={card.id} value={card.id.toString()}>
                                      {card.cardType} ending in {card.cardLastFour}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          )}

                          {/* Manual card entry fields */}
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Card Number"
                              name="cardNumber"
                              value={formData.cardNumber}
                              onChange={handleTextFieldChange}
                              required={!formData.creditCardId}
                              disabled={isLoading}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Name on Card"
                              name="nameOnCard"
                              value={formData.nameOnCard}
                              onChange={handleTextFieldChange}
                              required={!formData.creditCardId}
                              disabled={isLoading}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Expiry Date"
                              name="expiryDate"
                              value={formData.expiryDate}
                              onChange={handleTextFieldChange}
                              placeholder="MM/YY"
                              required={!formData.creditCardId}
                              disabled={isLoading}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="CVV"
                              name="cvv"
                              value={formData.cvv}
                              onChange={handleTextFieldChange}
                              required={!formData.creditCardId}
                              disabled={isLoading}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Amount"
                              name="amount"
                              type="number"
                              value={formData.amount}
                              onChange={handleTextFieldChange}
                              inputProps={{ min: 0.01, step: 0.01, max: getBalance(formData.currency) }}
                              required
                              disabled={isLoading}
                              helperText={`Available: ${formData.currency === 'EUR' ? '€' : '$'}${getBalance(formData.currency).toFixed(2)}`}
                              InputProps={{
                                startAdornment: formData.currency === 'EUR' ? '€' : '$',
                              }}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Description (Optional)"
                              name="description"
                              value={formData.description}
                              onChange={handleTextFieldChange}
                              multiline
                              rows={3}
                              disabled={isLoading}
                            />
                          </Grid>
                        </>
                      ) : (
                        <>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Beneficiary Name"
                              name="beneficiary"
                              value={formData.beneficiary}
                              onChange={handleTextFieldChange}
                              required
                              disabled={isLoading}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Account Number"
                              name="accountNumber"
                              value={formData.accountNumber}
                              onChange={handleTextFieldChange}
                              required
                              disabled={isLoading}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Name of Bank"
                              name="bankName"
                              value={formData.bankName}
                              onChange={handleTextFieldChange}
                              required
                              disabled={isLoading}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Swift / Routing Number"
                              name="swiftBic"
                              value={formData.swiftBic}
                              onChange={handleTextFieldChange}
                              required
                              disabled={isLoading}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Country"
                              name="country"
                              value={formData.country}
                              onChange={handleTextFieldChange}
                              required
                              disabled={isLoading}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Phone Number"
                              name="phoneNumber"
                              value={formData.phoneNumber}
                              onChange={handleTextFieldChange}
                              required
                              disabled={isLoading}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Address"
                              name="address"
                              value={formData.address}
                              onChange={handleTextFieldChange}
                              multiline
                              rows={2}
                              required
                              disabled={isLoading}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Amount"
                              name="amount"
                              type="number"
                              value={formData.amount}
                              onChange={handleTextFieldChange}
                              inputProps={{ min: 0.01, step: 0.01, max: getBalance(formData.currency) }}
                              required
                              disabled={isLoading}
                              helperText={`Available: ${formData.currency === 'EUR' ? '€' : '$'}${getBalance(formData.currency).toFixed(2)}`}
                              InputProps={{
                                startAdornment: formData.currency === 'EUR' ? '€' : '$',
                              }}
                            />
                          </Grid>
                        </>
                      )}

                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                          <Button
                            type="button"
                            onClick={handleSubmit}
                            variant="contained"
                            color="primary"
                            startIcon={<Payment />}
                            disabled={isLoading}
                            sx={{ py: 1.5 }}
                          >
                            {isLoading ? <CircularProgress size={20} /> : 'Withdraw Funds'}
                          </Button>
                          <Button variant="outlined" onClick={() => navigate('/dashboard')} disabled={isLoading}>Cancel</Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default Withdraw;
